import { Flex, PremiumLineItem, smBumps, Typo } from '@wrisk/ui-components'
import React, { useMemo } from 'react'

import { Promotion } from '../../../domain'
import { QuotePremiums } from '../../../hooks/premiums'
import {
  tFormats,
  TKeyBuilder,
  useWriskTranslation,
} from '../../../infrastructure/internationalisation'
import { toDateTime } from '../../../util/date'
import {
  additionalLineItems,
  coreLineItems,
  discountLineItems,
  feesLineItems,
  optionalLineItems,
} from './lineItems'

export const useStandardAdjustedPremium = ({
  currentPremiums,
  previousPremiums,
  promotion,
  tKey,
}: {
  currentPremiums: QuotePremiums
  previousPremiums?: QuotePremiums
  promotion?: Promotion
  tKey: TKeyBuilder
}) => {
  const { t } = useWriskTranslation()

  return useMemo(() => {
    const currentAmount = t(tFormats('currency.long'), {
      amount: currentPremiums.totals.discounted,
    })

    const previousAmount =
      previousPremiums &&
      t(tFormats('currency.long'), {
        amount: previousPremiums.totals.discounted,
      })

    const header = t(tKey(currentPremiums?.paymentSchedule, 'header'))

    const promo = promotion?.endAt
      ? t(tKey('promotion'), {
          date: t(tFormats('date.long'), { value: toDateTime(promotion.endAt) }),
        })
      : undefined

    const lineItems: PremiumLineItem[] = [
      ...coreLineItems(t, tKey, currentPremiums, previousPremiums),
      ...feesLineItems(t, tKey, currentPremiums),
      ...discountLineItems(t, tKey, currentPremiums, previousPremiums),
      ...optionalLineItems(t, currentPremiums, previousPremiums),
    ]

    const additionalItems = additionalLineItems(t, tKey, currentPremiums)

    const termTotalFormatted = t(tFormats('currency.long'), {
      amount: currentPremiums.totals.term,
    })

    const termTotalLabel = t(tKey('term-total'), { defaultValue: null })

    const termTotal = t(tKey('term-total-amount'), {
      amount: termTotalFormatted,
      defaultValue: null,
    })

    const showTermTotalSection =
      currentPremiums?.paymentSchedule === 'MONTHLY' &&
      termTotalLabel &&
      termTotalFormatted

    const extraInfo = (
      <Flex
        flexDirection='column'
        width={1}
        borderTopWidth={1}
        py={smBumps}
        gap={smBumps}
      >
        <Flex width={1}>
          <Typo typoSize='sm' fontWeight='bold'>
            {header}
          </Typo>
          <Typo typoSize='sm' fontWeight='bold'>
            {currentAmount}
          </Typo>
        </Flex>
        {showTermTotalSection && (
          <Flex width={1}>
            <Typo typoSize='sm'>{termTotalLabel}</Typo>
            <Typo typoSize='sm'>{termTotalFormatted}</Typo>
          </Flex>
        )}
      </Flex>
    )

    return {
      header,
      extraInfo,
      lineItems,
      additionalItems,
      currentAmount,
      previousAmount,
      termTotal,
      promo,
    }
  }, [t, tKey, currentPremiums, previousPremiums, promotion])
}
